<template>
  <div class="dashboard-container">
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="mb-2 page-title">
      <i class="bi bi-file-earmark-text"></i> Contact Person List
    </div>

    <div class="mb-2 d-flex justify-content-end align-items-center">
      <router-link to="/admin/contact-person/create" class="button button-primary">
        <i class="bi bi-plus-square"></i> Add New
      </router-link>
    </div>

    <table class="table mt-4">
      <thead>
        <tr>
          <th>ID</th>
          <th>Tournament</th>
          <th>Description</th>
          <th>Name</th>
          <th>Phone</th>
          <th>Email</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody v-if="contactPersons.length > 0">
        <tr
          v-for="(contactPerson, index) in contactPersons"
          :key="contactPerson.id"
        >
          <td>{{ index + 1 + (currentPage - 1) * perPage }}</td>
          <td>{{ contactPerson.tournament?.name ?? '-' }}</td>
          <td>{{ contactPerson.description ?? '-' }}</td>
          <td>{{ contactPerson.name ?? '-' }}</td>
          <td>{{ contactPerson.phone ?? '-' }}</td>
          <td>{{ contactPerson.email ?? '-' }}</td>

          <td class="action-column">
            <div class="btn-group" role="group">
              <button
                type="button"
                class="button button-primary"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="bi bi-card-checklist"></i> Action
              </button>

              <ul class="dropdown-menu">
                <li>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="editContactPerson(contactPerson.id)"
                  >
                    <i class="bi bi-pencil-square"></i> Edit
                  </a>
                </li>
              </ul>
            </div>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="7" class="text-center">No contact person found.</td>
        </tr>
      </tbody>
    </table>

    <nav v-if="totalPages > 1">
      <ul class="pagination pagination-lg justify-content-end">
        <li class="page-item" :class="{ disabled: !prevPageUrl }">
          <button
            class="page-link"
            type="button"
            @click="changePage(currentPage - 1)"
            :disabled="!prevPageUrl || loading"
          >
            <i class="bi bi-arrow-left-square"></i>
          </button>
        </li>

        <li
          v-for="page in totalPages"
          :key="page"
          class="page-item"
          :class="{ active: currentPage === page }"
        >
          <button
            class="page-link"
            type="button"
            @click="changePage(page)"
            :disabled="loading || currentPage === page"
          >
            {{ page }}
          </button>
        </li>

        <li class="page-item" :class="{ disabled: !nextPageUrl }">
          <button
            class="page-link"
            type="button"
            @click="changePage(currentPage + 1)"
            :disabled="!nextPageUrl || loading"
          >
            <i class="bi bi-arrow-right-square"></i>
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/config/api";

axios.defaults.baseURL = API.API_BASE_URL;

export default {
  name: "ContactPersonList",

  data() {
    return {
      contactPersons: [],
      searchQuery: "",
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      prevPageUrl: null,
      nextPageUrl: null,
      loading: false,
      progress: 0,
    };
  },

  mounted() {
    this.loadContactPersons();
  },

  methods: {
    async loadContactPersons(page = 1) {
      this.loading = true;

      try {
        const response = await axios.get("/tournament-contact-persons", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          params: {
            page,
            perPage: this.perPage,
            search: this.searchQuery.trim(),
          },
        });

        const {
          current_page,
          last_page,
          data,
          next_page_url,
          prev_page_url,
        } = response.data;

        this.contactPersons = data || [];
        this.currentPage = current_page || page;
        this.totalPages = last_page || 0;
        this.nextPageUrl = next_page_url || null;
        this.prevPageUrl = prev_page_url || null;
      } catch (error) {
        console.error("Error loading contact persons:", error);

        this.contactPersons = [];
        this.currentPage = 1;
        this.totalPages = 0;
        this.nextPageUrl = null;
        this.prevPageUrl = null;
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (this.loading) {
        return;
      }

      if (page < 1 || page > this.totalPages) {
        return;
      }

      if (page === this.currentPage) {
        return;
      }

      this.loadContactPersons(page);
    },

    searchClasses() {
      this.currentPage = 1;
      this.loadContactPersons(1);
    },

    editContactPerson(id) {
      this.$router.push({
        name: "EditContactPerson",
        params: { id },
      });
    },

    deleteMenu(id) {
      console.log("Delete menu with ID:", id);
    },
  },

  computed: {
    prevPageExists() {
      return this.currentPage > 1;
    },

    nextPageExists() {
      return this.currentPage < this.totalPages;
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  background-color: #ffffff;
  margin-top: 80px;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.table th,
.table td {
  padding: 10px;
}

.navbar {
  background-color: #1e2a57 !important;
}

.navbar-toggler-icon {
  background-color: white;
}

.nav-link {
  color: white !important;
}

.form-control {
  width: 250px;
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background-color: #388e3c;
  animation: loader-animation 1.5s infinite;
}

@keyframes loader-animation {
  0% {
    width: 0;
    background-color: #388e3c;
  }

  50% {
    width: 50%;
    background-color: #388e3c;
  }

  100% {
    width: 100%;
    background-color: #388e3c;
  }
}
</style>